import { currentUserId } from './meta';

export const OPEN_WIDGET_BUILDER_EVENT = 'output:internal:widgets:open-builder';

const STORAGE_VERSION = 1;
const MAX_DISMISSALS = 200;
const dismissalListeners = new Set();
let dismissalsVersion = 0;
let dismissalsCacheRaw = Symbol('unread');
let dismissalsCache = {};
let listeningForDismissalStorage = false;

export const storagePrefix = () => `output:widget-generation:${currentUserId() || 'anon'}`;
const dismissalsStorageKey = () => `${storagePrefix()}:dismissed-activity-cycles`;

function availableLocalStorage() {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

export function readJson(key, fallback) {
  const storage = availableLocalStorage();
  if (!storage) return fallback;

  try {
    const value = JSON.parse(storage.getItem(key));
    return value ?? fallback;
  } catch {
    return fallback;
  }
}

export function writeJson(key, value) {
  const storage = availableLocalStorage();
  if (!storage) return;

  try {
    storage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage-backed state remains best-effort when storage is unavailable.
  }
}

function storedDismissals() {
  let raw = null;
  try {
    raw = availableLocalStorage()?.getItem(dismissalsStorageKey()) ?? null;
  } catch {
    raw = null;
  }

  if (raw !== dismissalsCacheRaw) {
    dismissalsCacheRaw = raw;
    let stored = null;
    try {
      stored = JSON.parse(raw);
    } catch {
      stored = null;
    }
    dismissalsCache =
      stored?.version === STORAGE_VERSION && stored.dismissals && typeof stored.dismissals === 'object' ? stored.dismissals : {};
  }

  return dismissalsCache;
}

export function widgetGenerationActivityDismissed(cycleId) {
  return Boolean(cycleId && storedDismissals()[cycleId]);
}

function notifyDismissalListeners() {
  dismissalsVersion += 1;
  dismissalListeners.forEach((listener) => listener());
}

function handleDismissalStorage(event) {
  if (event.key && event.key !== dismissalsStorageKey()) return;

  dismissalsCacheRaw = Symbol('unread');
  notifyDismissalListeners();
}

function startDismissalStorageListener() {
  if (listeningForDismissalStorage || typeof window === 'undefined' || typeof window.addEventListener !== 'function') return;

  window.addEventListener('storage', handleDismissalStorage);
  listeningForDismissalStorage = true;
}

function stopDismissalStorageListener() {
  if (!listeningForDismissalStorage || dismissalListeners.size > 0) return;

  window.removeEventListener('storage', handleDismissalStorage);
  listeningForDismissalStorage = false;
}

export function subscribeToWidgetGenerationDismissals(listener) {
  dismissalListeners.add(listener);
  startDismissalStorageListener();
  return () => {
    dismissalListeners.delete(listener);
    stopDismissalStorageListener();
  };
}

export function widgetGenerationDismissalsVersion() {
  return dismissalsVersion;
}

export function recordWidgetGenerationActivityDismissal(cycleId, { workspaceId, agentChatId } = {}, recordedAt = Date.now()) {
  if (!cycleId) return;

  const dismissals = {
    ...storedDismissals(),
    [cycleId]: {
      workspaceId: workspaceId ? String(workspaceId) : null,
      agentChatId: agentChatId ? String(agentChatId) : null,
      recordedAt,
    },
  };
  const retainedDismissals = Object.fromEntries(
    Object.entries(dismissals)
      .sort(([, left], [, right]) => (right.recordedAt || 0) - (left.recordedAt || 0))
      .slice(0, MAX_DISMISSALS),
  );

  writeJson(dismissalsStorageKey(), { version: STORAGE_VERSION, dismissals: retainedDismissals });
  dismissalsCacheRaw = Symbol('unread');
  notifyDismissalListeners();
}

export function clearWidgetGenerationActivityDismissalsForChat(workspaceId, agentChatId) {
  if (!workspaceId || !agentChatId) return;

  const workspaceKey = String(workspaceId);
  const agentChatKey = String(agentChatId);
  const dismissals = Object.fromEntries(
    Object.entries(storedDismissals()).filter(
      ([, dismissal]) => dismissal.workspaceId !== workspaceKey || dismissal.agentChatId !== agentChatKey,
    ),
  );

  writeJson(dismissalsStorageKey(), { version: STORAGE_VERSION, dismissals });
  dismissalsCacheRaw = Symbol('unread');
  notifyDismissalListeners();
}
